"use client"

import {useTranslations} from "next-intl";
import {CopySvg} from "@/app/components/icons/CopySvg";
import {showAppToast} from "@/app/components/common/app-toast/showAppToast";
import * as React from "react";
import {useEffect, useState} from "react";
import {useAppSelector} from "@/lib/hooks";
import {AppSwitch} from "@/app/components/common/app-switch/AppSwitch";
import {isFcmConfigured} from "@/lib/firebase/config";
import {useWebPushRegistration} from "@/lib/firebase/webPush";
import {useDeactivatePushTokenMutation} from "@/lib/api/auth/pushTokensApi";
import {FCM_TOKEN_STORAGE_KEY, unregisterWebPushTokenLocally} from "@/lib/firebase/localToken";
import {
    trackButtonClicked,
    trackPushPermissionRequested,
    trackPushPermissionResult,
} from "@/lib/analytics/mixpanel-events";
import {usePathname} from "@/i18n/navigation";
import {toPageName} from "@/lib/analytics/page-name";

interface IProps {
    onLogOutClick: () => void;
}

/** Notifications are "on" for this browser when permission is granted and a token is stored. */
function readPushEnabled(): boolean {
    if (typeof window === "undefined" || typeof Notification === "undefined") return false;
    return Notification.permission === "granted" && !!localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
}

export default function AvatarPopoverContent({onLogOutClick}: IProps) {
    const t = useTranslations();
    const userId = useAppSelector(state => state.auth.userId)!;
    const pathname = usePathname();

    const registerToken = useWebPushRegistration();
    const [deactivatePushToken] = useDeactivatePushTokenMutation();
    const [pushSupported, setPushSupported] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushBusy, setPushBusy] = useState(false);

    // Browser-only state — resolve after mount to avoid SSR/hydration mismatch.
    useEffect(() => {
        setPushSupported(isFcmConfigured() && typeof Notification !== "undefined");
        setPushEnabled(readPushEnabled());
    }, []);

    const onCopyMyIdClick = async () => {
        // The avatar popover is the app's profile surface — the same one
        // `profile_opened` attaches to. The id itself is never sent: it is the
        // `user_id` super-property already, and the copy is what we're counting.
        trackButtonClicked({
            buttonName: 'copy_user_id',
            component: 'profile',
            page: toPageName(pathname),
        });
        try {
            await navigator.clipboard.writeText(userId);
            showAppToast({
                title: t('AvatarPopover.idCopied'),
                description: t('AvatarPopover.yourIdCopiedToBuffer'),
                type: 'positive',
            });
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    }

    const onPushToggle = async (next: boolean) => {
        if (pushBusy) return;
        setPushBusy(true);
        try {
            if (next) {
                if (Notification.permission === "denied") {
                    // Can't re-prompt once blocked — the player must re-enable in browser settings.
                    showAppToast({
                        title: t('AvatarPopover.notifications'),
                        description: t('AvatarPopover.notificationsBlocked'),
                        type: 'negative',
                    });
                    return;
                }
                // Only a genuine prompt is an event: a permission the browser has
                // already granted is not a request, and reporting it as one would
                // inflate the grant rate with players who never saw a prompt.
                let permission: NotificationPermission = Notification.permission;
                if (permission !== "granted") {
                    trackPushPermissionRequested();
                    permission = await Notification.requestPermission();
                    trackPushPermissionResult(permission);
                }
                if (permission !== "granted") return;
                await registerToken();
                setPushEnabled(true);
            } else {
                const token = await unregisterWebPushTokenLocally();
                if (token) {
                    await deactivatePushToken(token).unwrap();
                }
                setPushEnabled(false);
            }
        } catch (err) {
            console.error("[FCM] toggle failed", err);
            setPushEnabled(readPushEnabled());
        } finally {
            setPushBusy(false);
        }
    }

    return (
        <div>
            {pushSupported && (
                <div className={'text-white h-[52px] w-full flex items-center justify-between font-semibold leading-none px-[24px] py-[16px]'}>
                    {t('AvatarPopover.notifications')}
                    <AppSwitch
                        checked={pushEnabled}
                        disabled={pushBusy}
                        onCheckedChange={onPushToggle}
                    />
                </div>
            )}
            <button onClick={onCopyMyIdClick}
                    className={'text-white h-[52px] w-full flex items-center justify-between font-semibold leading-none cursor-pointer text-left px-[24px] py-[16px]'}>
                {t('AvatarPopover.copyMyId')}
                <span className={'text-noble-black-300'}>
                    <CopySvg width={20} height={20} />
                </span>
            </button>
            <button onClick={onLogOutClick}
                    className={'text-red-power-600 h-[52px] w-full block font-semibold leading-none cursor-pointer text-left px-[24px] py-[16px]'}>
                {t('AvatarPopover.logout')}
            </button>
        </div>
    )
}
